import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import {
  Mail, Phone, MapPin, Cake, Share2, Heart, UserCheck, Megaphone,
  CheckCircle2, XCircle, Loader2, UserPlus, BadgeCheck, Instagram, Facebook, Trophy, Cpu, Clock, Send,
} from 'lucide-react';

// Detail modal for a single competition entry. Shows everything the entrant
// submitted (contact details, address, social engagement, marketing opt-in)
// plus an account/invite action so admins can send a sign-up invitation.
export default function CompetitionEntryDetailModal({ entry, hasAccount, onClose, onInvited }) {
  const { toast } = useToast();
  const [inviting, setInviting] = useState(false);
  const [invitedAt, setInvitedAt] = useState(entry?.invited_at || null);

  if (!entry) return null;

  const isPaid = entry.competition_type === 'sim_rig_paid' || entry.is_paid;
  const email = (entry.email || '').toLowerCase();

  const sendInvite = async () => {
    if (!email) {
      toast({ title: 'No email on this entry', variant: 'destructive' });
      return;
    }
    setInviting(true);
    try {
      await base44.users.inviteUser(email, 'user');
      const now = new Date().toISOString();
      try {
        await base44.entities.CompetitionEntry.update(entry.id, { invited_at: now });
      } catch (_) { /* non-blocking — invite already sent */ }
      setInvitedAt(now);
      onInvited?.(entry.id, now);
      toast({ title: 'Sign-up invite sent', description: email });
    } catch (e) {
      toast({ title: 'Invite failed', description: e?.message || 'Could not send invite', variant: 'destructive' });
    } finally {
      setInviting(false);
    }
  };

  const Row = ({ label, children }) => (
    <div className="flex items-start gap-3 py-2 border-b border-white/5">
      <span className="text-white/30 text-[10px] font-bold tracking-widest w-32 shrink-0 pt-0.5">{label}</span>
      <span className="text-white/80 text-sm flex-1 min-w-0 break-words">{children ?? '—'}</span>
    </div>
  );

  const Social = ({ on, label, children }) => (
    <div className={`flex items-center gap-2 px-3 py-2 border ${on ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 bg-white/[0.02]'}`}>
      {on ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-white/20" />}
      <span className={`text-xs font-bold tracking-widest ${on ? 'text-green-400' : 'text-white/40'}`}>{label}</span>
      <span className="ml-auto text-white/50 text-xs">{children}</span>
    </div>
  );

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111115] border border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-black uppercase flex items-center gap-2 flex-wrap">
            {isPaid ? <Cpu className="w-4 h-4 text-primary" /> : <Trophy className="w-4 h-4 text-primary" />}
            {entry.first_name} {entry.last_name}
            <span className={`text-[10px] font-bold tracking-widest border px-2 py-1 ${isPaid ? 'text-primary border-primary/30 bg-primary/10' : 'text-white/60 border-white/15 bg-white/5'}`}>
              {isPaid ? `SIM RIG £${entry.paid_amount || 5}` : 'MONTHLY FREE'}
            </span>
            {entry.is_winner && (
              <span className="inline-flex items-center gap-1 text-green-400 text-[10px] font-bold tracking-widest border border-green-500/30 bg-green-500/5 px-2 py-1">
                <Trophy className="w-3 h-3" /> WINNER {entry.winner_month}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contact */}
          <section>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">CONTACT</p>
            <Row label="EMAIL"><a href={`mailto:${entry.email}`} className="text-primary/80 hover:text-primary inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{entry.email}</a></Row>
            <Row label="PHONE"><a href={`tel:${entry.phone}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="w-3.5 h-3.5" />{entry.phone}</a></Row>
            <Row label="DOB"><span className="inline-flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" />{fmtDate(entry.date_of_birth)}</span></Row>
            <Row label="ADDRESS"><span className="inline-flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5" />{[entry.address_line1, entry.address_city, entry.postcode].filter(Boolean).join(', ')}</span></Row>
            <Row label="EXPERIENCE">{entry.experience_preference === 'any' ? 'Any experience' : entry.experience_preference}</Row>
          </section>

          {/* Social engagement */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Megaphone className="w-3.5 h-3.5 text-primary" />
              <p className="text-white/30 text-[10px] font-bold tracking-widest">BOOST ENTRY · SOCIAL ENGAGEMENT (SELF-DECLARED)</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <Social on={entry.liked_post} label="LIKED POST"><Heart className="w-3 h-3" /></Social>
              <Social on={entry.shared_post} label="SHARED POST"><Share2 className="w-3 h-3" /></Social>
              <Social on={entry.followed_social} label="FOLLOWING"><UserCheck className="w-3 h-3" /></Social>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 mt-2">
              <Row label="INSTAGRAM">{entry.instagram_handle ? <span className="inline-flex items-center gap-1.5"><Instagram className="w-3.5 h-3.5" />{entry.instagram_handle}</span> : '—'}</Row>
              <Row label="TIKTOK">{entry.tiktok_handle || '—'}</Row>
              <Row label="FACEBOOK">{entry.facebook_handle ? <span className="inline-flex items-center gap-1.5"><Facebook className="w-3.5 h-3.5" />{entry.facebook_handle}</span> : '—'}</Row>
            </div>
            <p className="text-white/30 text-[11px] leading-relaxed mt-2">
              These ticks are self-certified by the entrant — we can't verify likes/shares/follows from the social platforms directly.
            </p>
          </section>

          {/* Entry meta */}
          <section>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">ENTRY DETAILS</p>
            <Row label="COMPETITION">{isPaid ? `Sim Rig Draw · ${entry.rig_draw_period || '—'}` : 'Free Monthly Draw'}</Row>
            <Row label="SOURCE">{entry.source || 'website'}</Row>
            <Row label="MARKETING">{entry.marketing_opt_in ? <span className="text-green-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Opted in</span> : <span className="text-white/40">Not opted in</span>}</Row>
            <Row label="ENTERED"><span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{fmtDateTime(entry.created_date)}</span></Row>
            {entry.draw_month && <Row label="DRAW MONTH">{entry.draw_month}</Row>}
            {entry.winner_month && <Row label="WON">{entry.winner_month}</Row>}
            {entry.notes && <Row label="NOTES">{entry.notes}</Row>}
          </section>

          {/* Account / invite */}
          <section className="border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-3.5 h-3.5 text-primary" />
              <p className="text-white/50 text-[10px] font-bold tracking-widest">ONLINE ACCOUNT</p>
            </div>
            {hasAccount ? (
              <div className="flex items-center gap-2 text-primary text-sm font-bold">
                <BadgeCheck className="w-4 h-4" /> Already has a registered account ({email})
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-white/50 text-xs">No online account yet for {email}.</p>
                <Button onClick={sendInvite} disabled={inviting} className="bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest h-9">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {invitedAt ? 'RESEND SIGN-UP INVITE' : 'SEND SIGN-UP INVITE'}
                </Button>
                {invitedAt && (
                  <span className="text-green-400/80 text-[11px] inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Invited {fmtDate(invitedAt)}
                  </span>
                )}
              </div>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}